// Georeferenced image overlays for Mapbox GL (image source + raster layer), below buildings/POIs.

const PREFIX = "tour-raster-overlay-";

function layerId(overlayId) {
  return `${PREFIX}${overlayId}-layer`;
}

function sourceId(overlayId) {
  return `${PREFIX}${overlayId}-src`;
}

function clampOpacity(n) {
  if (typeof n === "number" && Number.isFinite(n)) {
    return Math.min(1, Math.max(0, n));
  }
  return 1;
}

function isValidCoordinates(c) {
  return (
    Array.isArray(c) &&
    c.length === 4 &&
    c.every(
      (p) =>
        Array.isArray(p) &&
        p.length === 2 &&
        typeof p[0] === "number" &&
        typeof p[1] === "number" &&
        Number.isFinite(p[0]) &&
        Number.isFinite(p[1]),
    )
  );
}

/**
 * Remove all tour raster overlay layers/sources by scanning style (handles unknown ids after reload).
 * @param {import('mapbox-gl').Map} map
 * @param {string[]} [knownOverlayIds] - optional Prisma ids to remove first
 */
export function removeAllMapRasterOverlays(map, knownOverlayIds) {
  if (!map) return;
  try {
    if (Array.isArray(knownOverlayIds)) {
      for (const oid of knownOverlayIds) {
        const lid = layerId(oid);
        const sid = sourceId(oid);
        if (map.getLayer(lid)) map.removeLayer(lid);
        if (map.getSource(sid)) map.removeSource(sid);
      }
    }
    const layers = map.getStyle()?.layers || [];
    for (const l of layers) {
      if (typeof l.id === "string" && l.id.startsWith(PREFIX) && l.id.endsWith("-layer")) {
        if (map.getLayer(l.id)) map.removeLayer(l.id);
      }
    }
    const sources = map.getStyle()?.sources || {};
    for (const sid of Object.keys(sources)) {
      if (sid.startsWith(PREFIX) && sid.endsWith("-src")) {
        if (map.getSource(sid)) map.removeSource(sid);
      }
    }
  } catch (_) {
    /* style may be unavailable */
  }
}

/**
 * @param {object} opts
 * @param {import('mapbox-gl').Map} opts.map
 * @param {{ id: string, url: string, coordinates: unknown, order?: number, opacity?: number }[]} opts.overlays
 */
export function addMapRasterOverlays({ map, overlays }) {
  if (!map || !Array.isArray(overlays)) return;
  const valid = overlays
    .filter((o) => o && typeof o.url === "string" && o.url.length > 0 && isValidCoordinates(o.coordinates))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  for (const o of valid) {
    const sid = sourceId(o.id);
    const lid = layerId(o.id);
    try {
      if (map.getSource(sid)) map.removeSource(sid);
    } catch (_) {}
    map.addSource(sid, {
      type: "image",
      url: o.url,
      coordinates: o.coordinates,
    });
    map.addLayer({
      id: lid,
      type: "raster",
      source: sid,
      paint: { "raster-opacity": clampOpacity(o.opacity) },
    });
  }
}
